import { Button, Dropdown } from "components/ui";
import { CompanyContext } from "contexts/CompanyContext";
import React, { useContext, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import InputModern from "components/ui/Input/InputModern";
import { BsStars } from "react-icons/bs";
import ConfigPanelPostTypes from "../ConfigPanelPostTypes";
import ConfigPanelImageTypes from "../ConfigPanelImageTypes";
import useRandomContexts from "utils/hooks/useRandomContexts";
import { ScheduledPostContext } from "contexts/ScheduledPostContext";
import usePostGenerationQuantity from "utils/hooks/usePostGenerationQuantity";
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';
const PromptTemplate = ({
    generatePostsFromContexts,
    config,
    setConfig
}) => {

    const maxSelection = usePostGenerationQuantity();

    const [ query, setQuery ] = useState("");
    const [ isLoading, setIsLoading ] = useState(false);
    const [ suggestions, setSuggestions ] = useState([]);
    const [ isLoadingSuggestions, setIsLoadingSuggestions ] = useState(false);

    const { scheduledPosts } = useContext(ScheduledPostContext);
    const { generateRandomContexts } = useRandomContexts();
    const { company } = useContext(CompanyContext);
    const { t } = useTranslation();
    const { whitelabel } = useWhitelabel();

    const _removeSavedSuggestion = () => {
        let savedSuggestions = suggestions;
        savedSuggestions = savedSuggestions.filter(suggestion => suggestion?.toLowerCase() != query?.toLowerCase());
        setSuggestions(savedSuggestions);
        localStorage.setItem(`promptTemplate.suggestions.${company?.id}`, JSON.stringify(savedSuggestions));
    }

    const initialSuggestions = async (forceNoCharge = false) => {
        setIsLoadingSuggestions(true);
        setSuggestions(
            localStorage.getItem(`promptTemplate.suggestions.${company?.id}`)
                ? JSON.parse(localStorage.getItem(`promptTemplate.suggestions.${company?.id}`))
                : await generateRandomContexts(undefined, forceNoCharge ? false : !!suggestions?.length)
        );
        setIsLoadingSuggestions(false);
    }

    const regenerateSuggestions = async () => {
        setIsLoadingSuggestions(true);
        setSuggestions(await generateRandomContexts());
        setIsLoadingSuggestions(false);
    }

    // const handleOnSubmit = async (quantity = maxSelection) => {
    //     setIsLoading(true);
    //     _removeSavedSuggestion();
    //     try {
    //         let promptContexts = "";

    //         if (!config.prompts?.length) {
    //             const randomPrompt = getRandomPromptForNetwork(config.postType);
    //             if (randomPrompt) {
    //                 promptContexts = [randomPrompt.prompt];
    //             }
    //         } else {
    //             promptContexts = config.prompts.reduce((acc, curr) => acc + ". " + curr.prompt, ``);
    //         }

    //         promptContexts+=`. Based on this context, now: ${query}`;

    //         let contexts = ["", ...Array(quantity - 1).fill("")];
    //         contexts[0] = promptContexts;

    //         // await generatePostsFromContexts([promptContexts], [], quantity);
    //     } finally {
    //         setIsLoading(false);
    //     }
    // };

    const handleOnSubmit = async (quantity = maxSelection) => {
        _removeSavedSuggestion();
        generatePostsFromContexts([query], [], quantity);
    }

    useEffect(() => {
        if (suggestions?.length) {
            localStorage.setItem(`promptTemplate.suggestions.${company?.id}`, JSON.stringify(suggestions));
        }
    }, [suggestions]);

    useEffect(() => {
        setSuggestions([]);
        initialSuggestions(true);
    }, [company?.id]);
    
    return (
        <div className="w-full h-full flex items-start p-4 gap-4 py-16">
            <div className='relative flex flex-col items-center justify-center w-full gap-8 lg:gap-12'> 
                <h1 className="mega-title text-center text-4xl lg:text-5xl text-tertiary-500 dark:text-gray-400">
                    👋&nbsp; Hey{company?.name && `, ${company?.name}`}!
                </h1>
                {!scheduledPosts?.length && <h1 className="mega-title text-center text-4xl lg:text-5xl !-mt-16 lg:!-mt-20 text-tertiary-500 dark:text-gray-400">
                    <br/>Let's create your first posts.
                </h1>}

                {!!scheduledPosts?.length && <p className="text-base lg:text-lg text-tertiary-700 dark:text-gray-100 text-center">
                    <span className="hidden lg:inline-block">Ask {whitelabel?.name} to generate engaging social media posts.</span><br className="hidden lg:inline-block" />
                    Just select the <b>social network</b>, the <b>image type</b> and write what you want to create!
                </p>}
                <div className='relative bg-secondary-500 dark:bg-gray-700 rounded-2xl shadow-2xl mb-8 mt-2 w-full max-w-[1024px] mx-auto z-[2]'> 
                    <div className="flex flex-col-reverse lg:flex-row items-center">
                        <div className="flex items-center w-full lg:w-auto">
                            <ConfigPanelPostTypes 
                                postType={config?.postType}
                                setPostType={(postType) => setConfig({...config, postType: postType})}
                                selectedPrompts={config?.prompts}
                                onSelectPrompt={(prompt) => {
                                    let prompts = config?.prompts || [];
                                    if (prompts?.find(e => e.id == prompt.id)) {
                                        prompts = prompts.filter(e => e.id != prompt.id);
                                    } else {
                                        prompts = [...prompts, prompt];
                                    }
                                    setConfig({...config, prompts: prompts})
                                }}
                                parentClassName="flex-auto shrink-0"
                                className="justify-center shrink-0 ml-0 !pt-4 !pb-4 !px-4 !pl-5 !pr-5 !bg-secondary-500 dark:!bg-gray-700 !rounded-r-none border-r border-tertiary-100 dark:border-gray-600 !capitalize"
                            />
                            <ConfigPanelImageTypes 
                                config={config}
                                setConfig={setConfig}
                                parentClassName="flex-auto"
                                placement="bottom-end"
                                className="justify-center shrink-0 ml-0 !pt-4 !pb-4 !px-4 !pl-5 !pr-5 !bg-secondary-500 dark:!bg-gray-700 lg:!rounded-r-none !rounded-l-none border-r border-tertiary-100 dark:border-gray-600 !capitalize"
                            />
                        </div>
                        <InputModern
                            fieldsetClassName="!bg-transparent flex-1 w-full shrink-0"
                            className="text-center lg:text-left lg:!pl-5 lg:!pr-44 !px-5 !py-5 !h-auto !bg-transparent !text-sm !text-slate-800 dark:!text-slate-200 !rounded-none !ring-0 focus:!ring-0 focus:!border-gray-100 dark:focus:!border-gray-600 !ring-transparent !shadow-none focus:!shadow-none focus:!ring-transparent !border-b border-gray-100 dark:border-gray-600 lg:!border-b-none lg:!border-none lg:!border-0 lg:!border-transparent" 
                            value={query}
                            placeholder={t(`promptTemplate.placeholderQuery`)} 
                            onChange={(e) => setQuery(e.target.value)} 
                            onKeyUp={(e) => e.key == 'Enter' ? handleOnSubmit() : void 0}
                        />
                    </div>
                    <div className='p-0 lg:!absolute lg:right-2.5 lg:top-1/2 lg:translate-y-[-50%] flex items-center gap-2 w-full lg:w-auto'>
                        {/* <Button 
                            loading={isLoading}
                            disabled={isLoading || !query?.trim()}
                            variant="ai" 
                            size="md" 
                            className="flex gap-2 items-center !text-sm flex-1 justify-center"
                            onClick={() => handleOnSubmit()}
                            icon={<BsStars />}
                        >
                            <Trans i18nKey={`aiImage.generate`}>Generate </Trans> ({maxSelection})
                        </Button>  */}
                        <Dropdown
                            placement="bottom-end"
                            parentClassName="w-full lg:w-auto"
                            disabled={isLoading || !query?.trim()}
                            renderTitle={
                                <Button 
                                    loading={isLoading}
                                    disabled={isLoading || !query?.trim()}
                                    variant="ai" 
                                    size="md" 
                                    className="flex gap-2 items-center !text-sm flex-1 justify-center w-full !rounded-t-none lg:!rounded-t-2xl"
                                    icon={<BsStars />}
                                >
                                    <Trans i18nKey={`aiImage.generate`}>Generate</Trans>
                                </Button>
                            }
                        >
                            {[...Array(maxSelection).keys()].map((key) => (
                                <Dropdown.Item 
                                    key={key}
                                    onClick={() => handleOnSubmit(key+1)} 
                                    className="py-2"
                                >
                                    Generate {key+1} post{key != 0 && `s`}
                                </Dropdown.Item>
                            ))}
                        </Dropdown>
                    </div>
                </div>
                <div className="flex flex-col gap-6 items-center justify-center -mt-6">
                    {!!suggestions?.length && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 w-full max-w-5xl">
                            {suggestions?.map((suggestion, key) => (
                                <div
                                    key={key}
                                    onClick={() => setQuery(suggestion)} 
                                    className={`relative overflow-hidden rounded-xl shadow-lg transition-all duration-300 cursor-pointer 
                                               bg-gradient-to-br ${!!true ? 'from-primary-100 to-primary-300 dark:from-primary-600 dark:to-primary-900' : 
                                                                key % 3 === 1 ? 'from-tertiary-100 to-tertiary-300 dark:from-tertiary-600 dark:to-tertiary-900' : 
                                                                               'from-secondary-100 to-secondary-300 dark:from-secondary-600 dark:to-secondary-900'}`}
                                >
                                    <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white to-transparent opacity-30"></div>
                                    <div className="p-5">
                                        <div className="mb-1 text-xs uppercase tracking-wider font-semibold text-tertiary-700 dark:text-tertiary-300 opacity-70">Suggestion {key + 1}</div>
                                        <p className="text-tertiary-800 dark:text-tertiary-100 font-medium">"{suggestion}"</p>
                                        <div className="absolute bottom-3 right-3">
                                            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-white dark:bg-gray-700 bg-opacity-40 dark:bg-opacity-40">
                                                <BsStars className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <Button 
                        variant="ai"
                        className="mt-6 mb-6"
                        icon={<BsStars />}
                        onClick={regenerateSuggestions}
                        loading={isLoadingSuggestions}
                    >
                        {!!suggestions?.length ? `Regenerate` : `Generate`} suggestions
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default PromptTemplate;